import type { BbQueueManager } from '@Components/BigBrowse/bb2/BbQueueManager';
import { HamburgerButtonPresenter } from '@Components/SideDrawerRight/HamburgerButton/HamburgerButton.presenter';
import { ObservableProp, useClassStore } from '@Utils/useClassStore';
import { inject, injectable } from 'inversify';
import { computed } from 'mobx';

import { LocalStorageGateway } from '~gateways/local-storage';
import container from '~ioc/ioc';
import { NavigationStore } from '~stores/navigation';
import type { JourneyParameters } from '~types/navigation';

type SeenFilterType = 'brand' | 'date';

export type IProductVisited = {
  productName: string;
  productId: number;
  brandName: string;
  brandId?: number;
  productImage: string;
  brandImage?: string;
  path?: JourneyParameters;
  date: string;
};
type IProductsByDate = {
  date: string;
  products: IProductVisited[];
};
type IProductsByBrand = {
  brand: string;
  products: IProductVisited[];
};

@injectable()
export class SeenThingsPresenter {
  @inject(NavigationStore) private navigator2Store!: NavigationStore;
  @inject(LocalStorageGateway)
  private localStorageGateway!: LocalStorageGateway;
  visible = new ObservableProp(false);
  mode = new ObservableProp<SeenFilterType>('brand');
  brandAnimation = new ObservableProp(false);
  detailAnimation = new ObservableProp(true);
  selectedBrand = new ObservableProp<IProductsByBrand | undefined>(undefined);

  private _bBQueueManager?: BbQueueManager = undefined;
  private hPresenter?: HamburgerButtonPresenter = undefined;

  @computed get showEyeIcon(): boolean {
    return this.hPresenter?.showHamburger ?? false;
  }

  // Solves cicular dependecy
  bindBBQueueManager = (bbQueueManager: BbQueueManager) => {
    this._bBQueueManager = bbQueueManager;
  };

  bindHamburgerMenu = (hamburgerMenuPresenter: HamburgerButtonPresenter) => {
    this.hPresenter = hamburgerMenuPresenter;
  };

  public animationStates = {
    brand: {
      initial: {
        opacity: 1,
        left: 0,
        zIndex: 1000,
      },
      animate: {
        opacity: 0,
        left: -120,
        zIndex: -1,
      },
    },
    detail: {
      initial: {
        opacity: 1,
        right: 0,
        zIndex: 1000,
      },
      animate: {
        opacity: 0,
        right: -40,
        zIndex: -1,
      },
    },
  };

  @computed get productsByDate() {
    const products =
      this.localStorageGateway.getItem<IProductVisited[]>('products') ?? [];
    const productsSortedByDate: IProductsByDate[] = [];

    products.forEach((item) => {
      const currentProduct = productsSortedByDate.find(
        (el) => el.date === item.date
      );
      if (currentProduct) {
        currentProduct.products.push(item);
      } else {
        productsSortedByDate.push({
          date: item.date,
          products: [item],
        });
      }
    });

    // Sort products by date
    productsSortedByDate.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);

      const timeA = dateA.toISOString();
      const timeB = dateB.toISOString();

      if (timeA < timeB) {
        return -1;
      }
      if (timeA > timeB) {
        return 1;
      }

      return 0;
    });
    return productsSortedByDate.reverse();
  }

  @computed get productsByBrand() {
    const products =
      this.localStorageGateway.getItem<IProductVisited[]>('products') ?? [];

    const brandProducts: IProductsByBrand[] = [];
    products.forEach((item) => {
      const currentProduct = brandProducts.find(
        (el) => el.brand === item.brandName
      );
      if (currentProduct) {
        currentProduct.products.push(item);
      } else {
        brandProducts.push({
          brand: item.brandName,
          products: [item],
        });
      }
    });
    return brandProducts;
  }

  show = () => {
    this.hPresenter?.isOpen.set(false);
    this.visible.set(true);
  };

  hide = () => {
    this.visible.set(false);
    this.selectedBrand.set(undefined);
    this.mode.set('brand');
    this.closeBrandDetails();
  };

  switchModal = () => {
    this.visible.set(!this.visible.value);
    this.hPresenter?.isOpen.set(false);
  };

  openBrandDetails = (item: IProductsByBrand) => {
    this.selectedBrand.set(item);
    this.brandAnimation.set(true);
    this.detailAnimation.set(false);
  };

  closeBrandDetails = () => {
    this.brandAnimation.set(false);
    this.detailAnimation.set(true);
  };

  hidePreviousScreens = () => {
    const presentersLength = this._bBQueueManager!.presentersArray.length;
    const prevPresenter =
      presentersLength > 1
        ? this._bBQueueManager!.presentersArray[presentersLength - 2]
        : undefined;
    prevPresenter?.hidePlane();
  };

  navigateToProductDetail = async (path: JourneyParameters | undefined) => {
    if (path) {
      await this.navigator2Store.pushToDetailView(path);
      this.hidePreviousScreens();
      this.hide();
      this.closeBrandDetails();
    }
  };
}

export const useSeenThingsPresenter = () =>
  useClassStore<SeenThingsPresenter>(container.get(SeenThingsPresenter));
